import { ApiError, API_OK, codeFromStatus } from "./errors";
import type { ApiEnvelope, TokenPair } from "./types";

export interface ApiClientConfig {
  /** API root including the version prefix, e.g. `/api/v1`. */
  baseUrl: string;
  /** Reads the in-memory access token (never persisted to storage). */
  getAccessToken: () => string | null;
  /** Called after login/register and after a successful silent refresh. */
  setAccessToken: (token: string | null) => void;
  /** Called when the refresh token is gone/expired: drop the session and go to /login. */
  onSessionExpired: () => void;
  /** Injection point for tests. */
  fetchImpl: typeof fetch;
}

const DEFAULT_CONFIG: ApiClientConfig = {
  baseUrl: "/api/v1",
  getAccessToken: () => null,
  setAccessToken: () => undefined,
  onSessionExpired: () => undefined,
  fetchImpl: (...args: Parameters<typeof fetch>) => fetch(...args),
};

let config: ApiClientConfig = { ...DEFAULT_CONFIG };

export function configureApiClient(patch: Partial<ApiClientConfig>): void {
  config = { ...config, ...patch };
}

export function getApiClientConfig(): ApiClientConfig {
  return config;
}

/** Test helper: restore the default configuration. */
export function resetApiClient(): void {
  config = { ...DEFAULT_CONFIG };
}

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/**
 * Query 参数值。数组会被展开成重复 key（`segment=silent&segment=churned`），
 * 这是 FastAPI `list[...]` 查询参数的唯一合法形式。
 */
export type QueryValue =
  string | number | boolean | null | undefined | readonly (string | number)[];

export interface RequestOptions {
  method?: HttpMethod;
  query?: Record<string, QueryValue>;
  /** JSON 对象或 `FormData`（上传走后者：不设 Content-Type，让浏览器补 boundary）。 */
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Disable the 401 -> refresh -> replay dance (used by the auth endpoints themselves). */
  skipAuthRetry?: boolean;
}

export interface RequestResult<T> {
  data: T;
  requestId: string | null;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const base = config.baseUrl.replace(/\/$/, "");
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === "") continue;
        search.append(key, String(item));
      }
      continue;
    }
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

function newRequestId(): string | null {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return null;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isEnvelope(payload: unknown): payload is ApiEnvelope<unknown> {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "code" in payload &&
    typeof (payload as { code: unknown }).code === "string"
  );
}

function toApiError(response: Response, payload: unknown): ApiError {
  const requestId = response.headers.get("X-Request-ID");
  if (isEnvelope(payload)) {
    return new ApiError({
      status: response.status,
      code: payload.code || codeFromStatus(response.status),
      message: payload.message || response.statusText || "Request failed",
      requestId: payload.request_id ?? requestId,
      details: payload.data,
    });
  }
  return new ApiError({
    status: response.status,
    code: codeFromStatus(response.status),
    message: response.statusText || "Request failed",
    requestId,
    details: payload,
  });
}

/** 真正发请求：拼 header、发 fetch、把网络异常包成 ApiError。下载与 JSON 调用共用。 */
async function sendRequest(
  path: string,
  options: RequestOptions,
): Promise<{ response: Response; requestId: string | null }> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const token = config.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const requestId = newRequestId() ?? null;
  if (requestId) headers["X-Request-ID"] = requestId;

  let response: Response;
  try {
    response = await config.fetchImpl(buildUrl(path, options.query), {
      method,
      headers,
      credentials: "include",
      body:
        options.body === undefined
          ? undefined
          : isFormData
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiError({
      status: 0,
      code: "network_error",
      message: cause instanceof Error ? cause.message : "Network error",
      requestId,
    });
  }

  return { response, requestId };
}

async function requestOnce<T>(path: string, options: RequestOptions): Promise<RequestResult<T>> {
  const { response, requestId } = await sendRequest(path, options);
  const payload = await parseBody(response);

  if (!response.ok) throw toApiError(response, payload);

  if (isEnvelope(payload)) {
    if (payload.code !== API_OK) {
      throw new ApiError({
        status: response.status,
        code: payload.code,
        message: payload.message || "Request failed",
        requestId: payload.request_id ?? response.headers.get("X-Request-ID"),
        details: payload.data,
      });
    }
    return {
      data: payload.data as T,
      requestId: payload.request_id ?? response.headers.get("X-Request-ID") ?? requestId,
    };
  }

  return { data: payload as T, requestId: response.headers.get("X-Request-ID") ?? requestId };
}

/** Endpoints that must never trigger a silent refresh (they define the session). */
const SESSION_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Refresh the access token using the httpOnly `pl_refresh` cookie.
 * Single-flight: concurrent 401s share one refresh call, which matters because the
 * backend revokes the whole session when a rotated refresh token is replayed.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!inflightRefresh) {
    inflightRefresh = doRefresh().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

async function doRefresh(): Promise<string | null> {
  let response: Response;
  try {
    response = await config.fetchImpl(buildUrl("/auth/refresh"), {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      // The cookie lives on the /api/v1/auth path; an explicit null body lets the
      // backend fall back to reading it from the request.
      body: JSON.stringify({ refresh_token: null }),
    });
  } catch {
    return null;
  }

  if (!response.ok) return null;

  const payload = await parseBody(response);
  if (!isEnvelope(payload) || payload.code !== API_OK || !payload.data) return null;

  const tokens = payload.data as TokenPair;
  config.setAccessToken(tokens.access_token);
  return tokens.access_token;
}

/**
 * Single entry point for every API call:
 * unwraps the `{code,message,data}` envelope, and on 401 refreshes once and replays.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const skipRetry = options.skipAuthRetry || SESSION_PATHS.some((p) => path.startsWith(p));
  try {
    return (await requestOnce<T>(path, options)).data;
  } catch (error) {
    if (!(error instanceof ApiError) || !error.isUnauthorized || skipRetry) throw error;

    const token = await refreshAccessToken();
    if (!token) {
      config.onSessionExpired();
      throw error;
    }
    return (await requestOnce<T>(path, { ...options, skipAuthRetry: true })).data;
  }
}

async function requestRaw(path: string, options: RequestOptions): Promise<Response> {
  const { response } = await sendRequest(path, options);
  if (!response.ok) throw toApiError(response, await parseBody(response));
  return response;
}

/**
 * 拿原始响应（S9 导出文件下载用）。
 *
 * 文件走带鉴权接口（**没有公开直链**），浏览器必须带 Authorization，
 * 因此不能用 `<a href>` 直接跳，只能 fetch + blob。401 时同样刷新一次再重放。
 */
export async function apiRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const skipRetry = options.skipAuthRetry || SESSION_PATHS.some((p) => path.startsWith(p));
  try {
    return await requestRaw(path, options);
  } catch (error) {
    if (!(error instanceof ApiError) || !error.isUnauthorized || skipRetry) throw error;

    const token = await refreshAccessToken();
    if (!token) {
      config.onSessionExpired();
      throw error;
    }
    return requestRaw(path, { ...options, skipAuthRetry: true });
  }
}

/** 下载二进制文件，返回 blob 与服务端建议的文件名（Content-Disposition 解析）。 */
export async function apiBlob(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await apiRaw(path, options);
  return {
    blob: await response.blob(),
    fileName: fileNameFromDisposition(response.headers.get("Content-Disposition")),
  };
}

function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.replace(/"/g, "").trim() ?? null;
}
